import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Warehouse, Truck, Package, AlertTriangle, ChevronRight, ChevronLeft,
  Pencil, Trash2, CheckCircle2, X, Save, Filter, Eraser
} from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Stage 4C (Feb 2026): Super Admin Stock Master Tab
// Hierarchy enforced:
//   1. Depot Balance     → District-wise → Product drilldown
//   2. On-Road Stock     → Person-wise → Product drilldown (district filter)
//   3. Depot Received    → District-wise → Receipt detail drilldown
//   4. Stock Mismatch    → Person-wise → Mismatch detail (district filter)

const TABS = [
  { id: "depot",     en: "Depot Balance",   ta: "டிப்போ பேலன்ஸ்",     icon: Warehouse,      color: "bg-[#0A1A3F]" },
  { id: "onroad",    en: "On-Road Stock",   ta: "வழியில் ஸ்டாக்",      icon: Truck,          color: "bg-[#FF9500]" },
  { id: "received",  en: "Depot Received",  ta: "பெறப்பட்ட ஸ்டாக்",   icon: Package,        color: "bg-[#5856D6]" },
  { id: "mismatch",  en: "Stock Mismatch",  ta: "ஸ்டாக் வேறுபாடு",     icon: AlertTriangle,  color: "bg-[#FF3B30]" },
];

export default function AdminStock() {
  const { lang } = useLang();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = TABS.find(t => t.id === params.get("tab"))?.id || "depot";
  const [tab, setTab] = useState(initialTab);

  const setTabAndUrl = (id) => {
    setTab(id);
    const next = new URLSearchParams(params);
    next.set("tab", id);
    next.delete("drill");
    setParams(next, { replace: true });
  };

  const dispName = (it) => {
    if (!it) return "—";
    if (lang === "ta" && it.name_ta) return it.name_ta;
    return it.name || it.name_en || "—";
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-12">
      <TopBar title={lang === "ta" ? "ஸ்டாக் கட்டுப்பாடு" : "Stock"} onBack={() => nav("/admin")} />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5 fade-up">

        {/* TAB BAR */}
        <div className="flex gap-2 bg-white rounded-2xl p-1.5 soft-shadow overflow-x-auto" data-testid="stock-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} data-testid={`stock-tab-${t.id}`} onClick={() => setTabAndUrl(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition ${active ? `${t.color} text-white shadow-md` : "text-[#1D1D1F] hover:bg-gray-50"}`}>
                <Icon className="w-4 h-4" />
                {lang === "ta" ? t.ta : t.en}
              </button>
            );
          })}
        </div>

        {/* TAB CONTENT */}
        {tab === "depot"     && <DepotBalanceTab    lang={lang} dispName={dispName} isSuperAdmin={isSuperAdmin} />}
        {tab === "onroad"    && <OnRoadStockTab     lang={lang} dispName={dispName} isSuperAdmin={isSuperAdmin} />}
        {tab === "received"  && <DepotReceivedTab   lang={lang} dispName={dispName} />}
        {tab === "mismatch"  && <StockMismatchTab   lang={lang} dispName={dispName} isSuperAdmin={isSuperAdmin} />}
      </div>
    </div>
  );
}

// ============================================================
// 1. DEPOT BALANCE — District first, then Product drilldown
// ============================================================
const DepotBalanceTab = ({ lang, dispName, isSuperAdmin }) => {
  const [summary, setSummary] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [drill, setDrill] = useState(null);            // selected depot { id, name }
  const [drillData, setDrillData] = useState(null);    // { items: [...] }
  const [loading, setLoading] = useState(true);
  const [adjusting, setAdjusting] = useState(null);    // product row being adjusted

  useEffect(() => {
    Promise.all([
      api.get("/depot-stock-summary").then(r => r.data || []),
      api.get("/districts").then(r => r.data || []),
    ]).then(([s, d]) => { setSummary(s); setDistricts(d); }).finally(() => setLoading(false));
  }, []);

  const districtMap = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts]);

  const openDrill = (depot) => {
    setDrill(depot);
    setDrillData(null);
    api.get(`/depot-stock/${depot.id}`).then(r => setDrillData(r.data));
  };

  const closeDrill = () => { setDrill(null); setDrillData(null); };

  const reload = () => {
    api.get("/depot-stock-summary").then(r => setSummary(r.data || []));
    if (drill) api.get(`/depot-stock/${drill.id}`).then(r => setDrillData(r.data));
  };

  if (loading) return <div className="text-center text-[#86868B] py-12">Loading…</div>;

  // PRIORITY VIEW: District grid
  if (!drill) {
    const totalAll = summary.reduce((s, d) => s + (d.total_qty || 0), 0);
    return (
      <div className="space-y-4 fade-up">
        <div className="bg-gradient-to-r from-[#0A1A3F] to-[#0055FF] text-white rounded-2xl p-5 soft-shadow flex items-center justify-between" data-testid="db-hero">
          <div>
            <div className="text-xs opacity-80 uppercase tracking-wider font-bold">{lang === "ta" ? "மொத்த டிப்போ" : "Total Depot Balance"}</div>
            <div className="heading-font text-4xl font-bold mt-1">{totalAll.toLocaleString()} <span className="text-base opacity-80">{lang === "ta" ? "பெட்டிகள்" : "boxes"}</span></div>
          </div>
          <Warehouse className="w-12 h-12 opacity-40" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="db-grid">
          {summary.map(d => {
            const dist = districtMap[d.depot_id] || { id: d.depot_id, name: d.name };
            return (
              <button key={d.depot_id} data-testid={`db-district-${d.depot_id}`} onClick={() => openDrill(dist)}
                className="group bg-white rounded-2xl p-5 soft-shadow text-left hover:shadow-xl transition active:scale-[0.99] flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "மாவட்டம்" : "District"}</div>
                    <div className="heading-font font-bold text-lg text-[#1D1D1F] truncate">{dispName(dist)}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#0055FF]" />
                </div>
                <div className="mt-3 heading-font text-3xl font-bold text-[#0055FF]">{(d.total_qty || 0).toLocaleString()}</div>
                <div className="text-xs text-[#86868B] mt-1">{lang === "ta" ? "பெட்டிகள்" : "boxes"}</div>
                {d.low_count > 0 && (
                  <div className="mt-2 text-xs text-[#FF3B30] font-bold flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {d.low_count} {lang === "ta" ? "குறைந்த ஸ்டாக்" : "low stock"}
                  </div>
                )}
              </button>
            );
          })}
          {summary.length === 0 && <div className="col-span-full text-center text-[#86868B] py-8">{lang === "ta" ? "தரவு இல்ல" : "No data"}</div>}
        </div>
      </div>
    );
  }

  // DRILLDOWN VIEW: products inside selected district
  return (
    <div className="space-y-4 fade-up">
      <button onClick={closeDrill} data-testid="db-back-btn" className="flex items-center gap-1.5 text-[#0055FF] font-bold text-sm hover:underline">
        <ChevronLeft className="w-4 h-4" /> {lang === "ta" ? "மாவட்டங்கள்" : "All districts"}
      </button>

      <div className="bg-white rounded-2xl p-5 soft-shadow">
        <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "மாவட்டம்" : "District"}</div>
        <h2 className="heading-font text-2xl font-bold text-[#1D1D1F]" data-testid="db-drill-title">{dispName(drill)}</h2>
      </div>

      <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="db-product-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60">
              <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                <th className="px-4 py-3">{lang === "ta" ? "பொருள்" : "Product"}</th>
                <th className="px-4 py-3 text-right">{lang === "ta" ? "தற்போதைய பெட்டிகள்" : "Current Boxes"}</th>
                <th className="px-4 py-3 text-right">{lang === "ta" ? "பெறப்பட்டது (இன்று)" : "Received Today"}</th>
                <th className="px-4 py-3 text-right">{lang === "ta" ? "விற்றது (இன்று)" : "Sold Today"}</th>
                <th className="px-4 py-3">{lang === "ta" ? "நிலை" : "Status"}</th>
                {isSuperAdmin && <th className="px-4 py-3 text-right">{lang === "ta" ? "செயல்கள்" : "Actions"}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {drillData ? (drillData.items || []).map(p => (
                <tr key={p.product_id} className="hover:bg-gray-50/40" data-testid={`db-prod-${p.product_id}`}>
                  <td className="px-4 py-3 font-semibold heading-font">{lang === "ta" && p.name_ta ? p.name_ta : p.name_en || p.name}</td>
                  <td className="px-4 py-3 text-right font-bold text-lg">{(p.qty || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-[#34C759] font-bold">+{p.received_today || 0}</td>
                  <td className="px-4 py-3 text-right text-[#FF3B30] font-bold">-{p.sold_today || 0}</td>
                  <td className="px-4 py-3">
                    {p.low ? <span className="pill bg-red-50 text-red-700">{lang === "ta" ? "குறைவு" : "Low"}</span>
                           : <span className="pill bg-green-50 text-green-700">{lang === "ta" ? "சரி" : "OK"}</span>}
                  </td>
                  {isSuperAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button onClick={() => setAdjusting({ depot: drill, product: p })}
                          data-testid={`db-edit-${p.product_id}`}
                          className="p-2 rounded-lg hover:bg-blue-50 text-[#0055FF]" title={lang === "ta" ? "சரிசெய்" : "Adjust"}>
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              )) : (
                <tr><td colSpan={isSuperAdmin ? 6 : 5} className="px-4 py-12 text-center text-[#86868B]">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {adjusting && (
        <DepotAdjustModal
          depot={adjusting.depot}
          product={adjusting.product}
          lang={lang}
          onClose={() => setAdjusting(null)}
          onSaved={() => { setAdjusting(null); reload(); }}
        />
      )}
    </div>
  );
};

const DepotAdjustModal = ({ depot, product, lang, onClose, onSaved }) => {
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const d = parseInt(delta, 10);
    if (!d || !reason.trim()) {
      toast.error(lang === "ta" ? "எண் மற்றும் காரணம் தேவை" : "Quantity and reason required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/depot-stock/adjust", { depot_id: depot.id, product_id: product.product_id, delta: d, reason: reason.trim() });
      toast.success(lang === "ta" ? "சேமிக்கப்பட்டது" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-md w-full soft-shadow-md fade-up" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" && depot.name_ta ? depot.name_ta : depot.name}</div>
            <h3 className="heading-font font-bold text-xl">{lang === "ta" && product.name_ta ? product.name_ta : product.name_en || product.name}</h3>
            <div className="text-xs text-[#86868B] mt-1">{lang === "ta" ? "தற்போதைய" : "Current"}: <b>{product.qty}</b></div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "மாற்றம் (+ சேர்க்க / − குறைக்க)" : "Delta (+ to add / − to reduce)"}</label>
            <input type="number" value={delta} onChange={e => setDelta(e.target.value)} data-testid="db-adjust-delta"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-lg font-bold text-center" placeholder="e.g. 50 or -10" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "காரணம்" : "Reason"}</label>
            <input value={reason} onChange={e => setReason(e.target.value)} data-testid="db-adjust-reason"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold" placeholder={lang === "ta" ? "எடுத்துக்காட்டு: எண்ணிக்கை சரிசெய்தல்" : "e.g. recount fix"} />
          </div>
          <button onClick={submit} disabled={saving} data-testid="db-adjust-save"
            className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "…" : (lang === "ta" ? "சேமி" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// 2. ON-ROAD STOCK — Person first, then Product drilldown
// ============================================================
const OnRoadStockTab = ({ lang, dispName, isSuperAdmin }) => {
  const [summary, setSummary] = useState(null);
  const [stocks, setStocks] = useState([]);   // today's salesman_daily_stock records (for drilldown)
  const [districts, setDistricts] = useState([]);
  const [districtId, setDistrictId] = useState("");
  const [drill, setDrill] = useState(null);
  const [editing, setEditing] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const reload = () => {
    Promise.all([
      api.get("/depot/stock/summary").then(r => r.data),
      api.get("/stock", { params: { date_from: today, date_to: today, ...(districtId ? { district_id: districtId } : {}) } }).then(r => r.data || []),
    ]).then(([sum, st]) => { setSummary(sum); setStocks(st); });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [districtId]);
  useEffect(() => { api.get("/districts").then(r => setDistricts(r.data || [])); }, []);

  const districtMap = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts]);

  // Person-wise list — combine on_road from summary with district info from stocks
  const persons = useMemo(() => {
    if (!summary) return [];
    const list = (summary.on_road || []).filter(p => p.remaining > 0 || p.loaded > 0);
    // Attach district id from matching stocks record if any
    return list.map(p => {
      const rec = stocks.find(s => s.salesman_id === p.user_id);
      return {
        ...p,
        district_id: rec?.district_id,
        district_name: districtMap[rec?.district_id]?.name || "—",
        district_name_ta: districtMap[rec?.district_id]?.name_ta,
        record_id: rec?.id,
        record: rec,
      };
    }).filter(p => !districtId || p.district_id === districtId);
  }, [summary, stocks, districtId, districtMap]);

  const filteredDistricts = districts;

  if (!summary) return <div className="text-center text-[#86868B] py-12">Loading…</div>;

  // PRIORITY VIEW: person-wise
  if (!drill) {
    return (
      <div className="space-y-4 fade-up">
        <div className="bg-gradient-to-r from-[#FF9500] to-[#FF6B00] text-white rounded-2xl p-5 soft-shadow flex items-center justify-between" data-testid="or-hero">
          <div>
            <div className="text-xs opacity-80 uppercase tracking-wider font-bold">{lang === "ta" ? "வழியில்" : "On-Road Total"}</div>
            <div className="heading-font text-4xl font-bold mt-1">{(summary.on_road_total || 0).toLocaleString()} <span className="text-base opacity-80">{lang === "ta" ? "பெட்டிகள்" : "boxes"}</span></div>
          </div>
          <Truck className="w-12 h-12 opacity-40" />
        </div>

        {/* District filter */}
        <div className="bg-white rounded-2xl p-3 soft-shadow flex items-center gap-2 flex-wrap" data-testid="or-filter-bar">
          <span className="text-xs uppercase font-bold text-[#86868B] flex items-center gap-1 px-2"><Filter className="w-3 h-3" /> {lang === "ta" ? "மாவட்டம்" : "District"}</span>
          <button onClick={() => setDistrictId("")} data-testid="or-dist-all"
            className={`px-3 py-1.5 rounded-xl text-xs font-bold ${!districtId ? "bg-[#0055FF] text-white" : "bg-gray-100 text-[#1D1D1F]"}`}>
            {lang === "ta" ? "அனைத்தும்" : "All"}
          </button>
          {filteredDistricts.map(d => (
            <button key={d.id} onClick={() => setDistrictId(d.id)} data-testid={`or-dist-${d.id}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold ${districtId === d.id ? "bg-[#0055FF] text-white" : "bg-gray-100 text-[#1D1D1F]"}`}>
              {dispName(d)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="or-grid">
          {persons.map(p => (
            <button key={p.user_id} data-testid={`or-person-${p.user_id}`} onClick={() => setDrill(p)}
              className="group bg-white rounded-2xl p-5 soft-shadow text-left hover:shadow-xl transition active:scale-[0.99]">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{p.role === "manager" ? (lang === "ta" ? "மேனேஜர்" : "Manager") : (lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman")}</div>
                  <div className="heading-font font-bold text-lg text-[#1D1D1F] truncate">{p.name}</div>
                  <div className="text-xs text-[#86868B] truncate">{lang === "ta" && p.district_name_ta ? p.district_name_ta : p.district_name}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#FF9500]" />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[10px] uppercase text-[#86868B] font-bold">{lang === "ta" ? "ஏற்றம்" : "Loaded"}</div><div className="font-bold text-lg">{p.loaded}</div></div>
                <div><div className="text-[10px] uppercase text-[#86868B] font-bold">{lang === "ta" ? "விற்றது" : "Sold"}</div><div className="font-bold text-lg text-[#34C759]">{p.sold}</div></div>
                <div><div className="text-[10px] uppercase text-[#86868B] font-bold">{lang === "ta" ? "மீதி" : "Remain"}</div><div className="font-bold text-lg text-[#FF9500]">{p.remaining}</div></div>
              </div>
            </button>
          ))}
          {persons.length === 0 && <div className="col-span-full text-center text-[#86868B] py-8">{lang === "ta" ? "வழியில் ஸ்டாக் இல்ல" : "Nothing on-road"}</div>}
        </div>
      </div>
    );
  }

  // DRILLDOWN: products with the selected person
  const rec = drill.record;
  return (
    <div className="space-y-4 fade-up">
      <button onClick={() => setDrill(null)} data-testid="or-back-btn" className="flex items-center gap-1.5 text-[#FF9500] font-bold text-sm hover:underline">
        <ChevronLeft className="w-4 h-4" /> {lang === "ta" ? "அனைத்து நபர்களும்" : "All people"}
      </button>
      <div className="bg-white rounded-2xl p-5 soft-shadow flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{drill.role === "manager" ? "Manager" : "Salesman"}</div>
          <h2 className="heading-font text-2xl font-bold text-[#1D1D1F]" data-testid="or-drill-title">{drill.name}</h2>
          <div className="text-xs text-[#86868B] mt-1">{lang === "ta" && drill.district_name_ta ? drill.district_name_ta : drill.district_name}</div>
        </div>
        {isSuperAdmin && rec && (
          <button onClick={() => setEditing(rec)} data-testid="or-edit-btn"
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1.5">
            <Pencil className="w-4 h-4" /> {lang === "ta" ? "திருத்து" : "Edit"}
          </button>
        )}
      </div>

      {!rec && <div className="bg-white rounded-2xl p-8 text-center text-[#86868B] soft-shadow">{lang === "ta" ? "இன்றைய பதிவு இல்ல" : "No daily stock record for today"}</div>}

      {rec && (
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="or-product-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">{lang === "ta" ? "பொருள்" : "Product"}</th>
                  <th className="px-4 py-3 text-right">{lang === "ta" ? "ஏற்றம்" : "Loaded"}</th>
                  <th className="px-4 py-3 text-right">{lang === "ta" ? "விற்றது" : "Sold"}</th>
                  <th className="px-4 py-3 text-right">{lang === "ta" ? "மீதி" : "Remaining"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(rec.items || []).map((it, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/40">
                    <td className="px-4 py-3 font-semibold heading-font">{lang === "ta" && it.name_ta ? it.name_ta : it.name_en || it.product_id}</td>
                    <td className="px-4 py-3 text-right font-bold">{it.loaded_qty || 0}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#34C759]">{it.sold_qty || 0}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#FF9500]">{it.remaining_qty ?? Math.max((it.loaded_qty || 0) - (it.sold_qty || 0), 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <DailyStockEditModal record={editing} lang={lang} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
};

// ============================================================
// 3. DEPOT RECEIVED — District first, then receipt detail
// ============================================================
const DepotReceivedTab = ({ lang, dispName }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    api.get("/depot/received", { params: { days } }).then(r => setData(r.data));
  }, [days]);
  useEffect(() => { api.get("/districts").then(r => setDistricts(r.data || [])); }, []);

  const districtMap = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts]);

  // Group by depot_id
  const byDistrict = useMemo(() => {
    if (!data) return [];
    const groups = {};
    for (const it of (data.items || [])) {
      const did = it.depot_id;
      if (!groups[did]) groups[did] = { depot_id: did, depot_name: it.depot_name, depot_name_ta: it.depot_name_ta, items: [], total: 0 };
      groups[did].items.push(it);
      groups[did].total += Math.abs(it.delta || 0);
    }
    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [data]);

  if (!data) return <div className="text-center text-[#86868B] py-12">Loading…</div>;

  // PRIORITY: district grid
  if (!drill) {
    return (
      <div className="space-y-4 fade-up">
        <div className="bg-gradient-to-r from-[#5856D6] to-[#7B79E0] text-white rounded-2xl p-5 soft-shadow flex items-center justify-between" data-testid="dr-hero">
          <div>
            <div className="text-xs opacity-80 uppercase tracking-wider font-bold">{lang === "ta" ? `${days} நாட்களில் பெறப்பட்டது` : `Received in ${days} days`}</div>
            <div className="heading-font text-4xl font-bold mt-1">{(data.total_received || 0).toLocaleString()} <span className="text-base opacity-80">{lang === "ta" ? "பெட்டிகள்" : "boxes"}</span></div>
          </div>
          <Package className="w-12 h-12 opacity-40" />
        </div>

        <div className="bg-white rounded-2xl p-3 soft-shadow flex items-center gap-2 flex-wrap" data-testid="dr-period-bar">
          <span className="text-xs uppercase font-bold text-[#86868B] px-2">{lang === "ta" ? "காலம்" : "Period"}</span>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`dr-days-${d}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold ${days === d ? "bg-[#5856D6] text-white" : "bg-gray-100 text-[#1D1D1F]"}`}>
              {d} {lang === "ta" ? "நாட்கள்" : "days"}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dr-grid">
          {byDistrict.map(g => {
            const dist = districtMap[g.depot_id] || { id: g.depot_id, name: g.depot_name, name_ta: g.depot_name_ta };
            return (
              <button key={g.depot_id} data-testid={`dr-district-${g.depot_id}`} onClick={() => setDrill(g)}
                className="group bg-white rounded-2xl p-5 soft-shadow text-left hover:shadow-xl transition active:scale-[0.99]">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "மாவட்டம்" : "District"}</div>
                    <div className="heading-font font-bold text-lg text-[#1D1D1F] truncate">{dispName(dist)}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#5856D6]" />
                </div>
                <div className="mt-3 heading-font text-3xl font-bold text-[#5856D6]">+{g.total.toLocaleString()}</div>
                <div className="text-xs text-[#86868B] mt-1">{g.items.length} {lang === "ta" ? "பதிவுகள்" : "entries"}</div>
              </button>
            );
          })}
          {byDistrict.length === 0 && <div className="col-span-full text-center text-[#86868B] py-8">{lang === "ta" ? "தரவு இல்ல" : "No receipts"}</div>}
        </div>
      </div>
    );
  }

  // DRILLDOWN
  return (
    <div className="space-y-4 fade-up">
      <button onClick={() => setDrill(null)} data-testid="dr-back-btn" className="flex items-center gap-1.5 text-[#5856D6] font-bold text-sm hover:underline">
        <ChevronLeft className="w-4 h-4" /> {lang === "ta" ? "அனைத்து மாவட்டங்களும்" : "All districts"}
      </button>
      <div className="bg-white rounded-2xl p-5 soft-shadow flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "மாவட்டம்" : "District"}</div>
          <h2 className="heading-font text-2xl font-bold" data-testid="dr-drill-title">{lang === "ta" && drill.depot_name_ta ? drill.depot_name_ta : drill.depot_name}</h2>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-[#86868B] font-bold">{lang === "ta" ? "மொத்தம்" : "Total"}</div>
          <div className="heading-font font-bold text-3xl text-[#5856D6]">+{drill.total.toLocaleString()}</div>
        </div>
      </div>
      <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="dr-detail-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60">
              <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                <th className="px-4 py-3">{lang === "ta" ? "தேதி" : "Date / Time"}</th>
                <th className="px-4 py-3">{lang === "ta" ? "பொருள்" : "Product"}</th>
                <th className="px-4 py-3 text-right">{lang === "ta" ? "பெட்டிகள்" : "Boxes"}</th>
                <th className="px-4 py-3">{lang === "ta" ? "காரணம்" : "Reason"}</th>
                <th className="px-4 py-3">{lang === "ta" ? "பதிவு செய்தவர்" : "By"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {drill.items.map((it, idx) => (
                <tr key={idx} className="hover:bg-gray-50/40">
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{(it.ts || "").replace("T", " ").slice(0, 16)}</td>
                  <td className="px-4 py-3 font-semibold heading-font">{lang === "ta" && it.product_name_ta ? it.product_name_ta : it.product_name}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#34C759]">+{Math.abs(it.delta || 0)}</td>
                  <td className="px-4 py-3 text-[#86868B]">{it.reason || "—"}</td>
                  <td className="px-4 py-3">{it.by_user_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// 4. STOCK MISMATCH — Person first, then mismatch detail
// ============================================================
const StockMismatchTab = ({ lang, dispName, isSuperAdmin }) => {
  const [stocks, setStocks] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [districtId, setDistrictId] = useState("");
  const [drill, setDrill] = useState(null);
  const [editing, setEditing] = useState(null);
  const [days, setDays] = useState(7);

  const reload = () => {
    const today = new Date();
    const from = new Date(); from.setDate(today.getDate() - days);
    const params = { date_from: from.toISOString().slice(0, 10), date_to: today.toISOString().slice(0, 10) };
    if (districtId) params.district_id = districtId;
    api.get("/stock", { params }).then(r => setStocks(r.data || []));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [districtId, days]);
  useEffect(() => { api.get("/districts").then(r => setDistricts(r.data || [])); }, []);

  const districtMap = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts]);

  // Group by salesman_id, only those with mismatch != 0
  const persons = useMemo(() => {
    const groups = {};
    for (const r of stocks) {
      if ((r.total_mismatch || 0) === 0) continue;
      const k = r.salesman_id;
      if (!groups[k]) groups[k] = {
        salesman_id: k, salesman_name: r.salesman_name, salesman_role: r.salesman_role,
        district_id: r.district_id, district: districtMap[r.district_id],
        records: [], total_mismatch: 0,
      };
      groups[k].records.push(r);
      groups[k].total_mismatch += (r.total_mismatch || 0);
    }
    return Object.values(groups).sort((a, b) => Math.abs(b.total_mismatch) - Math.abs(a.total_mismatch));
  }, [stocks, districtMap]);

  const totalAll = persons.reduce((s, p) => s + Math.abs(p.total_mismatch), 0);

  // PRIORITY: person grid
  if (!drill) {
    return (
      <div className="space-y-4 fade-up">
        <div className="bg-gradient-to-r from-[#FF3B30] to-[#FF6B5C] text-white rounded-2xl p-5 soft-shadow flex items-center justify-between" data-testid="sm-hero">
          <div>
            <div className="text-xs opacity-80 uppercase tracking-wider font-bold">{lang === "ta" ? "மொத்த வேறுபாடு" : "Total Mismatch"}</div>
            <div className="heading-font text-4xl font-bold mt-1">{totalAll.toLocaleString()} <span className="text-base opacity-80">{lang === "ta" ? "பெட்டிகள்" : "boxes"}</span></div>
            <div className="text-xs opacity-80 mt-1">{persons.length} {lang === "ta" ? "நபர்" : "people"} • {days} {lang === "ta" ? "நாள்" : "day"}</div>
          </div>
          <AlertTriangle className="w-12 h-12 opacity-40" />
        </div>

        <div className="bg-white rounded-2xl p-3 soft-shadow flex items-center gap-2 flex-wrap" data-testid="sm-filter-bar">
          <span className="text-xs uppercase font-bold text-[#86868B] px-2 flex items-center gap-1"><Filter className="w-3 h-3" /> {lang === "ta" ? "மாவட்டம்" : "District"}</span>
          <button onClick={() => setDistrictId("")} data-testid="sm-dist-all"
            className={`px-3 py-1.5 rounded-xl text-xs font-bold ${!districtId ? "bg-[#FF3B30] text-white" : "bg-gray-100 text-[#1D1D1F]"}`}>
            {lang === "ta" ? "அனைத்தும்" : "All"}
          </button>
          {districts.map(d => (
            <button key={d.id} onClick={() => setDistrictId(d.id)} data-testid={`sm-dist-${d.id}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold ${districtId === d.id ? "bg-[#FF3B30] text-white" : "bg-gray-100 text-[#1D1D1F]"}`}>
              {dispName(d)}
            </button>
          ))}
          <span className="ml-auto text-xs uppercase font-bold text-[#86868B] px-2">{lang === "ta" ? "காலம்" : "Period"}</span>
          {[1, 7, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`sm-days-${d}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold ${days === d ? "bg-[#FF3B30] text-white" : "bg-gray-100 text-[#1D1D1F]"}`}>
              {d}d
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sm-grid">
          {persons.map(p => (
            <button key={p.salesman_id} data-testid={`sm-person-${p.salesman_id}`} onClick={() => setDrill(p)}
              className="group bg-white rounded-2xl p-5 soft-shadow text-left hover:shadow-xl transition active:scale-[0.99] border-l-4 border-[#FF3B30]">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{p.salesman_role === "manager" ? "Manager" : "Salesman"}</div>
                  <div className="heading-font font-bold text-lg text-[#1D1D1F] truncate">{p.salesman_name}</div>
                  <div className="text-xs text-[#86868B] truncate">{dispName(p.district)}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#FF3B30]" />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="heading-font text-3xl font-bold text-[#FF3B30]">{p.total_mismatch > 0 ? "+" : ""}{p.total_mismatch}</span>
                <span className="text-xs text-[#86868B]">{lang === "ta" ? "பெட்டிகள்" : "boxes"} • {p.records.length} {lang === "ta" ? "நாள்" : "day"}</span>
              </div>
            </button>
          ))}
          {persons.length === 0 && <div className="col-span-full text-center text-[#86868B] py-8">{lang === "ta" ? "எந்த வேறுபாடும் இல்ல" : "No mismatches"}</div>}
        </div>
      </div>
    );
  }

  // DRILLDOWN
  return (
    <div className="space-y-4 fade-up">
      <button onClick={() => setDrill(null)} data-testid="sm-back-btn" className="flex items-center gap-1.5 text-[#FF3B30] font-bold text-sm hover:underline">
        <ChevronLeft className="w-4 h-4" /> {lang === "ta" ? "அனைத்து நபர்களும்" : "All people"}
      </button>
      <div className="bg-white rounded-2xl p-5 soft-shadow">
        <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{drill.salesman_role === "manager" ? "Manager" : "Salesman"}</div>
        <h2 className="heading-font text-2xl font-bold" data-testid="sm-drill-title">{drill.salesman_name}</h2>
        <div className="text-xs text-[#86868B] mt-1">{dispName(drill.district)}</div>
      </div>

      {drill.records.map(rec => (
        <div key={rec.id} className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid={`sm-record-${rec.id}`}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "தேதி" : "Date"}</div>
              <div className="heading-font font-bold text-lg">{rec.date}</div>
              {rec.mismatch_resolved && <div className="text-xs text-[#34C759] font-bold flex items-center gap-1 mt-1"><CheckCircle2 className="w-3 h-3" /> {lang === "ta" ? "சரிசெய்யப்பட்டது" : "Resolved"} • {rec.mismatch_resolved_by_name}</div>}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-[#86868B] font-bold">{lang === "ta" ? "மொத்த வேறுபாடு" : "Total Mismatch"}</div>
              <div className="heading-font font-bold text-2xl text-[#FF3B30]">{rec.total_mismatch > 0 ? "+" : ""}{rec.total_mismatch}</div>
            </div>
            {isSuperAdmin && (
              <div className="flex gap-1.5">
                <button onClick={() => setEditing(rec)} data-testid={`sm-edit-${rec.id}`}
                  className="p-2 rounded-lg hover:bg-blue-50 text-[#0055FF]" title="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                {!rec.mismatch_resolved && (
                  <button onClick={async () => {
                    const msg = lang === "ta" ? "மீதி = ஏற்றம் − விற்பனை. தொடரவா?" : "Auto-set returned = expected? Mismatch will become 0. Action will be logged.";
                    if (!window.confirm(msg)) return;
                    try {
                      await api.post(`/admin/daily-stock/${rec.id}/resolve-mismatch`);
                      toast.success(lang === "ta" ? "சரிசெய்யப்பட்டது" : "Resolved");
                      reload();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }} data-testid={`sm-resolve-${rec.id}`}
                    className="p-2 rounded-lg hover:bg-green-50 text-[#34C759]" title={lang === "ta" ? "சரிசெய்" : "Resolve"}>
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                )}
                <button onClick={async () => {
                  const msg = lang === "ta" ? `இந்த பதிவை நீக்கவா? ${rec.date}` : `Delete this stock record?\n${rec.date}\nAction will be logged.`;
                  if (!window.confirm(msg)) return;
                  try {
                    await api.delete(`/admin/daily-stock/${rec.id}`);
                    toast.success(lang === "ta" ? "நீக்கப்பட்டது" : "Deleted");
                    reload();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }} data-testid={`sm-delete-${rec.id}`}
                  className="p-2 rounded-lg hover:bg-red-50 text-[#FF3B30]" title={lang === "ta" ? "நீக்கு" : "Delete"}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-2.5">{lang === "ta" ? "பொருள்" : "Product"}</th>
                  <th className="px-4 py-2.5 text-right">{lang === "ta" ? "ஏற்றம்" : "Loaded"}</th>
                  <th className="px-4 py-2.5 text-right">{lang === "ta" ? "விற்றது" : "Sold"}</th>
                  <th className="px-4 py-2.5 text-right">{lang === "ta" ? "திரும்பியது" : "Returned"}</th>
                  <th className="px-4 py-2.5 text-right">{lang === "ta" ? "வேறுபாடு" : "Difference"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(rec.items || []).filter(it => (it.mismatch || 0) !== 0).map((it, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/40">
                    <td className="px-4 py-2.5 font-semibold">{lang === "ta" && it.name_ta ? it.name_ta : it.name_en || it.product_id}</td>
                    <td className="px-4 py-2.5 text-right">{it.loaded_qty || 0}</td>
                    <td className="px-4 py-2.5 text-right text-[#34C759]">{it.sold_qty || 0}</td>
                    <td className="px-4 py-2.5 text-right">{it.returned_qty ?? "—"}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-[#FF3B30]">{(it.mismatch || 0) > 0 ? "+" : ""}{it.mismatch}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {editing && (
        <DailyStockEditModal record={editing} lang={lang} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
};

// ============================================================
// Daily-stock edit modal (used in On-Road + Mismatch tabs)
// ============================================================
const DailyStockEditModal = ({ record, lang, onClose, onSaved }) => {
  const [items, setItems] = useState(() => (record.items || []).map(it => ({
    product_id: it.product_id,
    name: lang === "ta" && it.name_ta ? it.name_ta : (it.name_en || it.name || it.product_id),
    loaded_qty: it.loaded_qty ?? 0,
    returned_qty: it.returned_qty ?? "",
  })));
  const [notes, setNotes] = useState(record.notes || "");
  const [saving, setSaving] = useState(false);

  const update = (idx, key, val) => {
    const next = [...items]; next[idx] = { ...next[idx], [key]: val }; setItems(next);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/daily-stock/${record.id}`, {
        items: items.map(i => ({
          product_id: i.product_id,
          loaded_qty: parseInt(i.loaded_qty, 10) || 0,
          returned_qty: i.returned_qty === "" || i.returned_qty === null ? null : parseInt(i.returned_qty, 10),
        })),
        notes: notes || null,
      });
      toast.success(lang === "ta" ? "சேமிக்கப்பட்டது" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto soft-shadow-md fade-up" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-5 border-b border-gray-100 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{record.date}</div>
            <h3 className="heading-font font-bold text-xl">{lang === "ta" ? "தினசரி ஸ்டாக் திருத்து" : "Edit Daily Stock"}</h3>
            <div className="text-xs text-[#86868B] mt-1">{record.salesman_name}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100" data-testid="ds-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase font-bold text-[#86868B]">
            <div className="col-span-6">{lang === "ta" ? "பொருள்" : "Product"}</div>
            <div className="col-span-3 text-right">{lang === "ta" ? "ஏற்றம்" : "Loaded"}</div>
            <div className="col-span-3 text-right">{lang === "ta" ? "திரும்பியது" : "Returned"}</div>
          </div>
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`ds-edit-item-${idx}`}>
              <div className="col-span-6 font-semibold text-sm truncate">{it.name}</div>
              <input type="number" value={it.loaded_qty} onChange={e => update(idx, "loaded_qty", e.target.value)}
                className="col-span-3 bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm font-semibold text-center" />
              <input type="number" value={it.returned_qty} onChange={e => update(idx, "returned_qty", e.target.value)}
                placeholder="—"
                className="col-span-3 bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm font-semibold text-center" />
            </div>
          ))}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "குறிப்புகள்" : "Notes"}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows="2" data-testid="ds-edit-notes"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2.5 text-sm font-semibold resize-none" />
          </div>
          <button onClick={submit} disabled={saving} data-testid="ds-edit-save"
            className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "…" : (lang === "ta" ? "சேமி" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
};
